import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Pencil, Trash2, FileText, Loader2 } from "lucide-react";
import { EditLoanDialog } from "@/components/loan/EditLoanDialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate, typeLabel } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { LateFeeButton } from "@/components/LateFeeButton";
import { LoanDetailView } from "@/components/loan/LoanDetailView";
import { ReminderToggle } from "@/components/loan/EmailLog";
import { PaymentDialog } from "@/components/loan/PaymentDialog";
import { PayoffLetterButton } from "@/components/loan/PayoffLetterButton";
import { SendPayLinkButton } from "@/components/loan/SendPayLinkButton";
import { CertificatePanel } from "@/components/loan/CertificatePanel";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { money } from "@/lib/format";

export default function LoanDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [dlg, setDlg] = useState({ open: false, payment: null });
  const [voiding, setVoiding] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [waiving, setWaiving] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonErr, setReasonErr] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [stmtKey, setStmtKey] = useState(0);
  const navigate = useNavigate();

  const generateNow = async () => {
    setGenBusy(true);
    try {
      const { data: s } = await api.post(`/loans/${id}/statements`);
      toast.success(s.created ? `Statement for ${fmtDate(s.period_start)} – ${fmtDate(s.period_end)} generated (unsent)` : "Statement for today already exists");
      setStmtKey((k) => k + 1);
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setGenBusy(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/loans/${id}`);
      toast.success("Loan deleted");
      navigate("/admin/loans");
    } catch (e) {
      toast.error(apiError(e).message);
      setDeleteOpen(false);
    }
  };

  const confirmWaive = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setReasonErr("A reason is required to waive a fee"); return; }
    try {
      await api.post(`/late-fees/${waiving.id}/waive`, { reason });
      toast.success(`Late fee for installment ${waiving.installment} waived`);
      setWaiving(null);
      load();
    } catch (err) {
      const { message, fields } = apiError(err);
      setReasonErr(fields.reason || message);
    }
  };

  const load = useCallback(() => {
    api.get(`/loans/${id}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e).message));
  }, [id]);
  useEffect(load, [load]);

  const confirmVoid = async () => {
    try {
      const { data: d } = await api.post(`/payments/${voiding.id}/void`);
      setData(d);
      toast.success("Payment voided — balances recalculated from ledger");
    } catch (e) {
      toast.error(apiError(e).message);
    } finally {
      setVoiding(null);
    }
  };

  if (error) return <div className="py-20 text-center text-muted-foreground" data-testid="loan-not-found">{error}</div>;
  if (!data) return <div className="py-20 text-center text-muted-foreground" data-testid="loan-loading">Loading loan…</div>;
  const { loan, summary } = data;

  return (
    <div data-testid="admin-loan-detail">
      <Link to="/admin/loans" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-loans">
        <ArrowLeft className="h-4 w-4" /> All loans
      </Link>
      <PageHeader
        eyebrow={`${loan.loan_number} · ${typeLabel(loan.interest_type)}`}
        title={<span className="flex flex-wrap items-center gap-3">{loan.borrower_name} <StatusBadge status={summary.status} testid="loan-status-badge" /></span>}
        subtitle={<>{loan.borrower_email} · Opened {fmtDate(loan.start_date)} · <span className="num">{money(loan.principal_cents)}</span></>}
        actions={<>
          <ReminderToggle key={loan.id} loan={loan} onChanged={load} />
          {summary.status !== "paid_off" && <PayoffLetterButton loan={loan} admin onSent={load} />}
          {summary.status !== "paid_off" && <SendPayLinkButton loanId={loan.id} refreshKey={data} onSent={load} />}
          <Button variant="ghost" onClick={() => setEditOpen(true)} data-testid="edit-loan-button"><Pencil /> Edit</Button>
          <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)} data-testid="delete-loan-button"><Trash2 /> Delete</Button>
          <Button variant="outline" onClick={generateNow} disabled={genBusy} data-testid="generate-statement-button">{genBusy ? <Loader2 className="animate-spin" /> : <FileText />} Generate statement now</Button>
          <LateFeeButton onDone={load} />
          <Button onClick={() => setDlg({ open: true, payment: null })} data-testid="record-payment-button"><Plus /> Record payment</Button>
        </>}
      />
      <CertificatePanel loanId={loan.id} admin refreshKey={data} />
      <LoanDetailView data={data} onEdit={(p) => setDlg({ open: true, payment: p })} onVoid={setVoiding}
        onWaive={(f) => { setWaiving(f); setReason(""); setReasonErr(""); }} statementsKey={stmtKey} />

      <EditLoanDialog open={editOpen} onOpenChange={setEditOpen} loan={loan} locked={data.has_payments} onSaved={load} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-loan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete {loan.loan_number}?</AlertDialogTitle>
            <AlertDialogDescription>Only loans without any payments can be deleted. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm-button">Delete loan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!waiving} onOpenChange={(o) => !o && setWaiving(null)}>
        <AlertDialogContent data-testid="waive-fee-dialog">
          <form onSubmit={confirmWaive} className="space-y-4">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display text-2xl">Waive late fee</AlertDialogTitle>
              <AlertDialogDescription>{waiving && `Installment ${waiving.installment} · ${money(waiving.outstanding_cents)} outstanding will be removed from the balance.`}</AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="waive-reason-input" />
            {reasonErr && <p className="text-xs font-medium text-destructive" data-testid="error-reason">{reasonErr}</p>}
            <AlertDialogFooter>
              <AlertDialogCancel type="button" data-testid="waive-cancel-button">Cancel</AlertDialogCancel>
              <Button type="submit" data-testid="waive-confirm-button">Waive fee</Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>

      <PaymentDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} loanId={loan.id}
        payment={dlg.payment} summary={summary} onSaved={setData} />

      <AlertDialog open={!!voiding} onOpenChange={(o) => !o && setVoiding(null)}>
        <AlertDialogContent data-testid="void-payment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Void this payment?</AlertDialogTitle>
            <AlertDialogDescription>
              {voiding && `${money(voiding.amount_cents)} received ${fmtDate(voiding.date)} will be removed from the ledger and every balance will be recomputed. Already-assessed late fees are not re-charged.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="void-cancel-button">Keep payment</AlertDialogCancel>
            <AlertDialogAction onClick={confirmVoid} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="void-confirm-button">Void payment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
